import { parseDbTimestamp } from "@repo/shared";
import type { StartQuizAttemptResponse, SubmitQuizAttemptResponse } from "@repo/shared/types";
import { CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./product-assessment-quiz-modal.i18n";

type ProductAssessmentQuizModalProps = {
  assessmentLabel: string;
  attempt: StartQuizAttemptResponse;
  onSubmit: (
    answers: Array<{ questionId: string; selectedOptionId: string }>,
  ) => Promise<SubmitQuizAttemptResponse>;
  /** Called when the runner is dismissed (after result, or cancel-confirmed). */
  onClose: () => void;
};

/**
 * Quiz runner: one question at a time, Previous/Next navigation, Finish submits
 * all answers, then a pass/fail result screen with attempt-availability info.
 * Cancelling keeps the server-side attempt open (start is idempotent), so a
 * reopen resumes the same drawn questions.
 */
export function ProductAssessmentQuizModal({
  assessmentLabel,
  attempt,
  onSubmit,
  onClose,
}: ProductAssessmentQuizModalProps) {
  const localize = useLocalize();
  const { formatDate: formatRegionalDate } = useRegionalFormat();
  const t = useT(T);

  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitQuizAttemptResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const questions = attempt.questions;
  const question = questions[questionIndex];
  const isLastQuestion = questionIndex === questions.length - 1;
  const currentAnswer = question ? answers[question.id] : undefined;

  const formatDate = (iso: string) => formatRegionalDate(parseDbTimestamp(iso));

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (result) {
      onClose();
    } else if (!isSubmitting) {
      setShowCancelConfirm(true);
    }
  };

  const handleFinish = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await onSubmit(
        questions.map((q) => ({ questionId: q.id, selectedOptionId: answers[q.id] })),
      );
      setResult(response);
    } catch (error) {
      log.error({ err: error }, "failed to submit quiz attempt");
      setSubmitError(t.submitFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAvailabilityInfo = () => {
    if (!result || result.passed) return null;
    const { availability } = result;
    if (availability.canAttempt) {
      if (availability.remaining == null) return null;
      return (
        <p className="text-sm text-muted-foreground">
          {t.attemptsRemaining(availability.remaining)}
        </p>
      );
    }
    if (availability.cooldownUntil) {
      return (
        <p className="text-sm text-muted-foreground">
          {t.retryAvailableOn(formatDate(availability.cooldownUntil))}
        </p>
      );
    }
    return <p className="text-sm text-muted-foreground">{t.noAttemptsRemaining}</p>;
  };

  return (
    <>
      <Dialog open onOpenChange={handleOpenChange}>
        {/* The quiz is submitted; the dialog now shows its result. */}
        <DialogContent guardDismiss={result === null}>
          {result ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {result.passed ? (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive" />
                  )}
                  {result.passed ? t.passedTitle : t.failedTitle}
                </DialogTitle>
                <DialogDescription>{result.passed ? t.passedBody : t.failedBody}</DialogDescription>
              </DialogHeader>
              {renderAvailabilityInfo()}
              <DialogFooter>
                <Button onClick={onClose}>{t.close}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{assessmentLabel}</DialogTitle>
                <DialogDescription>
                  {t.questionOf(questionIndex + 1, questions.length)}
                </DialogDescription>
              </DialogHeader>
              {question && (
                <div className="space-y-4">
                  <div className="font-medium">{localize(question.labelI18n)}</div>
                  <RadioGroup
                    value={currentAnswer ?? ""}
                    onValueChange={(value) =>
                      setAnswers((prev) => ({ ...prev, [question.id]: value }))
                    }
                    disabled={isSubmitting}
                    // QL-119 — these options are bordered cards, not bare rows: the
                    // row min-height gives them their tap target, but they still need
                    // a real gap or the borders butt together.
                    className="gap-2"
                  >
                    {question.options.map((option) => (
                      <OptionRow key={option.id} className="rounded-lg border pl-3">
                        <RadioGroupItem value={option.id} id={`quiz-option-${option.id}`} />
                        <Label
                          htmlFor={`quiz-option-${option.id}`}
                          className="flex-1 font-normal cursor-pointer py-3 pr-3"
                        >
                          {localize(option.labelI18n)}
                        </Label>
                      </OptionRow>
                    ))}
                  </RadioGroup>
                  {submitError && <p className="text-sm text-destructive">{submitError}</p>}
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setQuestionIndex((i) => Math.max(0, i - 1))}
                  disabled={questionIndex === 0 || isSubmitting}
                >
                  {t.previous}
                </Button>
                {isLastQuestion ? (
                  <Button onClick={handleFinish} disabled={!currentAnswer} loading={isSubmitting}>
                    {t.finish}
                  </Button>
                ) : (
                  <Button
                    onClick={() => setQuestionIndex((i) => Math.min(questions.length - 1, i + 1))}
                    disabled={!currentAnswer || isSubmitting}
                  >
                    {t.next}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title={t.cancelTitle}
        description={t.cancelBody}
        confirmText={t.cancelConfirm}
        cancelText={t.cancelDismiss}
        onConfirm={onClose}
      />
    </>
  );
}
